import { apiErrorName } from '@/lib/recepthor-api';
import { Button, HelperText, InputRow, Modal, Select } from '@/ui';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import './OrgModales.css';

// The API sends the email inside the invitation's transaction, so on
// EmailDeliveryFailed no membership was created and a retry creates one, with
// no duplicate. Every other failure gets a generic message.
function inviteFailureMessage(err) {
  if (apiErrorName(err?.body) === 'EmailDeliveryFailed') {
    return 'No se pudo enviar el correo de invitación. La membresía no quedó creada. Intenta nuevamente.';
  }
  return 'No se pudo enviar la invitación. Intenta nuevamente.';
}

export function InvitarMiembroModal({
  open,
  onClose,
  onSubmit,
  templates = [],
}) {
  const [correo, setCorreo] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => emailRef.current?.focus(), 50);
      setTemplateId(templates[0]?.id ?? '');
    }
  }, [open, templates]);

  const correoError = touched && !correo.trim();

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (!correo.trim()) return;
    setError('');
    setSending(true);
    try {
      await onSubmit?.({ correo: correo.trim(), templateId });
      handleClose();
    } catch (err) {
      setError(inviteFailureMessage(err));
    } finally {
      setSending(false);
    }
  }

  function handleClose() {
    setCorreo('');
    setTemplateId('');
    setTouched(false);
    setError('');
    onClose?.();
  }
  // Closing mid-request would hide the outcome of the invite.
  function close() {
    if (!sending) handleClose();
  }

  return (
    <Modal open={open} onClose={close}>
      <Modal.Header title="Invitar miembro" onClose={close} />
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <InputRow label="Correo">
            <input
              ref={emailRef}
              type="email"
              className={`form-input mono${correoError ? ' input-error' : ''}`}
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="nombre@ejemplo.cl"
              aria-required="true"
              aria-invalid={correoError}
            />
          </InputRow>
          <InputRow label="Rol">
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </InputRow>
          {correoError ? (
            <HelperText kind="err">El correo es requerido.</HelperText>
          ) : error ? (
            <HelperText kind="err">{error}</HelperText>
          ) : (
            <HelperText>
              Recibirá un correo con un enlace para activar su cuenta. La
              invitación expira en 7 días.
            </HelperText>
          )}
        </Modal.Body>
        <Modal.Foot>
          <Button
            variant="ghost"
            type="button"
            onClick={close}
            disabled={sending}
          >
            Cancelar
          </Button>
          <Button variant="seal" type="submit" disabled={sending}>
            {sending ? 'Enviando…' : 'Enviar invitación'}
          </Button>
        </Modal.Foot>
      </form>
    </Modal>
  );
}

InvitarMiembroModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  templates: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  ),
};

export default InvitarMiembroModal;
